/**
 * load-projects.js
 * Domain use case for loading project collections with layered fallbacks.
 */

export function createLoadProjects({
  loaders = [],
  onError = () => {},
  normalizer = defaultNormalizeProjects,
} = {}) {
  if (!Array.isArray(loaders) || loaders.length === 0) {
    throw new Error('At least one loader is required');
  }

  const normalizedLoaders = loaders.map((entry, index) => {
    if (typeof entry === 'function') {
      return { name: `loader${index + 1}`, load: entry };
    }
    if (entry && typeof entry.load === 'function') {
      return { name: entry.name || `loader${index + 1}`, load: entry.load };
    }
    throw new Error('Each loader must be a function or an object with load()');
  });

  return async function loadProjects(context = {}) {
    for (let i = 0; i < normalizedLoaders.length; i += 1) {
      const { name, load } = normalizedLoaders[i];
      try {
        const result = await load(context);
        const projects = normalizer(result, { name });
        if (Array.isArray(projects)) {
          return projects;
        }
      } catch (error) {
        safeInvoke(onError, { error, name, attempt: i, context });
      }
    }
    return [];
  };
}

function defaultNormalizeProjects(value) {
  if (!value) {
    return null;
  }
  if (Array.isArray(value)) {
    return value;
  }
  return null;
}

function safeInvoke(handler, payload) {
  try {
    handler(payload);
  } catch (error) {
    // Swallow handler errors to keep load pipeline resilient.
  }
}

if (typeof window !== 'undefined') {
  window.AgileProDomain = window.AgileProDomain || {};
  window.AgileProDomain.projects = window.AgileProDomain.projects || {};
  window.AgileProDomain.projects.createLoadProjects = createLoadProjects;
}
